import { isDEVMODE, globalStorage, domStorage } from '../_globals'
import gsap from 'gsap'
import { Observer } from 'gsap/Observer'
import NavMobile from './nav-mobile'
import { toggleHeaderTheme } from '../utils'
gsap.registerPlugin(Observer)

const MENU_BREAKPOINT = 768
const MEGAMENU_VIEWPORT_MARGIN = 100

export default class Header {
  constructor() {
    this.DOM = { el: domStorage.header }
    // Brand logo
    this.DOM.brand = this.DOM.el.querySelector('.brand')
    // Toggle for mobile version
    this.DOM.toggle = this.DOM.el.querySelector('.NavToggle')
    // Navigation
    this.DOM.navContainer = this.DOM.el.querySelector('.NavContainer')
    this.DOM.navMain = this.DOM.el.querySelector('.Nav.--main')
    this.DOM.navButtons = this.DOM.el.querySelector('.Nav.--buttons')
    this.DOM.navMobile = this.DOM.el.querySelector('.NavMobile')
    this.DOM.megamenuItems = this.DOM.navMain?.querySelectorAll('li.--megamenu') ?? []

    this.navMobile = this.DOM.navMobile ? new NavMobile(this.DOM.navMobile) : null

    this.setEvents()
  }

  isDesktop() {
    return window.innerWidth >= MENU_BREAKPOINT
  }

  positionMegamenu(e) {
    if (!this.isDesktop()) return

    const item = e?.currentTarget?.closest?.('li.--megamenu') ?? e
    const trigger = item.querySelector('.NavLink.--megamenu-trigger')
    const megamenu = item.querySelector('.NavMegamenu')
    const arrow = megamenu?.querySelector('.NavMegamenu__arrow')

    if (!trigger || !megamenu || !arrow) return

    const itemRect = item.getBoundingClientRect()
    const triggerRect = trigger.getBoundingClientRect()
    const megamenuWidth = megamenu.offsetWidth
    const arrowWidth = arrow.offsetWidth
    const triggerCenterX = triggerRect.left + triggerRect.width / 2
    const viewportWidth = window.innerWidth
    const maxLeft = viewportWidth - MEGAMENU_VIEWPORT_MARGIN - megamenuWidth
    let megamenuLeftViewport = triggerCenterX - megamenuWidth / 2

    megamenuLeftViewport = Math.max(MEGAMENU_VIEWPORT_MARGIN, Math.min(megamenuLeftViewport, maxLeft))

    const arrowLeft = triggerCenterX - megamenuLeftViewport - arrowWidth / 2

    megamenu.style.left = `${megamenuLeftViewport - itemRect.left}px`
    arrow.style.left = `${arrowLeft}px`
  }

  positionAllMegamenus() {
    if (!this.isDesktop()) return

    this.DOM.megamenuItems.forEach((item) => this.positionMegamenu(item))
  }

  setEvents() {
    const { overlay } = domStorage
    const { toggle } = this.DOM

    this.positionMegamenu = this.positionMegamenu.bind(this)
    this.positionAllMegamenus = this.positionAllMegamenus.bind(this)

    if (this.DOM.megamenuItems.length) {
      this.resetLevel2Active = this.resetLevel2Active.bind(this)
      this.onLevel2Enter = this.onLevel2Enter.bind(this)
      this.onLevel3Enter = this.onLevel3Enter.bind(this)
      this.cancelPendingLevel2 = this.cancelPendingLevel2.bind(this)
      this.level2SwitchTimer = null

      this.DOM.megamenuItems.forEach((item) => {
        item.addEventListener('mouseenter', this.positionMegamenu)

        item.querySelectorAll('.NavMegamenu__level2-item.--has-children').forEach((level2Item) => {
          level2Item.addEventListener('mouseenter', this.onLevel2Enter)
          level2Item.addEventListener('mouseleave', this.cancelPendingLevel2)
        })

        item.querySelectorAll('.NavMegamenu__level3--desktop').forEach((panel) => {
          panel.addEventListener('mouseenter', this.onLevel3Enter)
        })

        item.querySelector('.NavMegamenu')?.addEventListener('mouseleave', this.resetLevel2Active)
      })
    }

    // Toggle
    toggle.addEventListener('click', (e) => {
      e.preventDefault()
      globalStorage.menuOpen === true ? this.close() : this.open()
    })

    // Open Mobile
    globalStorage.openMobileMenu = () => this.open()

    // Close Mobile
    globalStorage.closeMobileMenu = () => this.close()

    // Close panels
    globalStorage.closeSubmenu = () => {
      this.navMobile?.reset()
      this.resetAllMegamenuPanels()
    }
    globalStorage.closeAllSubmenus = () => globalStorage.closeSubmenu()

    // Overlay
    overlay.addEventListener('click', () => globalStorage.menuOpen && this.close())

    // Scroll
    this.setObserver()

    // Resize
    this.resizeTimeout = setTimeout(() => {}, 0)
    this.onResize = this.onResize.bind(this)
    this.windowWidthResize = window.innerWidth
    window.addEventListener('resize', this.onResize)

    if (this.isDesktop()) {
      requestAnimationFrame(() => this.positionAllMegamenus())
    }
  }

  updateHeaderMobileTheme(isOpen) {
    if (this.isDesktop()) {
      this.DOM.el.classList.remove('--white')
      return
    }

    if (isOpen) {
      this.DOM.el.classList.remove('--light')
      this.DOM.el.classList.add('--white')
    } else {
      this.DOM.el.classList.remove('--white')
      toggleHeaderTheme()
    }
  }

  open() {
    globalStorage.menuOpen = true
    domStorage.body.classList.add('--show-menu')
    this.updateHeaderMobileTheme(true)
  }

  close() {
    globalStorage.menuOpen = false
    domStorage.body.classList.remove('--show-menu')
    this.updateHeaderMobileTheme(false)
    this.navMobile?.reset()
    this.resetAllMegamenuPanels()
  }

  resetAllMegamenuPanels() {
    this.DOM.megamenuItems.forEach((item) => {
      item.querySelectorAll('.NavMegamenu__level2-item.--active, .NavMegamenu__level2-item.--open').forEach((level2) => {
        level2.classList.remove('--active', '--open')
      })

      item.querySelectorAll('.NavMegamenu__level3--desktop.--active').forEach((panel) => {
        panel.classList.remove('--active')
      })
    })
  }

  setActiveLevel2Panel(megamenu, panelIndex) {
    megamenu.querySelectorAll('.NavMegamenu__level2-item.--active').forEach((item) => {
      item.classList.remove('--active')
    })

    megamenu.querySelectorAll('.NavMegamenu__level3--desktop.--active').forEach((panel) => {
      panel.classList.remove('--active')
    })

    const level2Item = megamenu.querySelector(`.NavMegamenu__level2-item[data-panel="${panelIndex}"]`)
    const level3Panel = megamenu.querySelector(`.NavMegamenu__level3--desktop[data-panel="${panelIndex}"]`)

    level2Item?.classList.add('--active')
    level3Panel?.classList.add('--active')
  }

  resetLevel2Active(e) {
    if (!this.isDesktop()) return

    clearTimeout(this.level2SwitchTimer)
    const megamenu = e.currentTarget
    const firstPanel = megamenu.querySelector('.NavMegamenu__level2-item.--has-children')

    if (!firstPanel) return

    this.setActiveLevel2Panel(megamenu, firstPanel.dataset.panel)
  }

  onLevel2Enter(e) {
    if (!this.isDesktop()) return

    const megamenu = e.currentTarget.closest('.NavMegamenu')
    const panelIndex = e.currentTarget.dataset.panel

    if (!megamenu || panelIndex === undefined) return

    // Hover intent : on ne commute le panneau de droite qu'après un vrai survol.
    // Traverser la liste (ou aller en diagonale vers le panneau) ne déclenche plus rien.
    clearTimeout(this.level2SwitchTimer)
    this.level2SwitchTimer = setTimeout(() => {
      this.setActiveLevel2Panel(megamenu, panelIndex)
    }, 140)
  }

  cancelPendingLevel2() {
    clearTimeout(this.level2SwitchTimer)
  }

  onLevel3Enter(e) {
    if (!this.isDesktop()) return

    const megamenu = e.currentTarget.closest('.NavMegamenu')
    const panelIndex = e.currentTarget.dataset.panel

    if (!megamenu || panelIndex === undefined) return

    this.setActiveLevel2Panel(megamenu, panelIndex)
  }

  setObserver() {
    const { body } = domStorage
    const { el } = this.DOM

    // Pendant le scroll, la page defile sous un curseur immobile : sans garde,
    // le :hover s'active "tout seul" quand la barre passe sous la souris.
    // On desarme les interactions du header pendant le defilement (+150ms).
    // Le garde ne se relâche PLUS au timer : avec l'inertie Lenis, la page continue de
    // glisser et le header peut réapparaître SOUS un curseur immobile -> le mégamenu
    // s'ouvrait "tout seul" pendant la remontée (effet ressort, retours Sandro/Amélie).
    // On ne relâche le garde qu'au premier VRAI déplacement de souris (delta > 2px).
    // Fermeture du mégamenu au clic sur un lien (retour Amélie 8/07) : avec les
    // transitions taxi.js il n'y a pas de rechargement, le menu restait ouvert sous le
    // curseur. Au clic : verrou CSS (--nav-lock) qui masque les panneaux, levé au
    // premier vrai déplacement de souris (le :hover ne peut pas rouvrir avant).
    el.addEventListener('click', (e) => {
      const lien = e.target.closest('.NavMegamenu a, .NavMain a, li.--megamenu > a')
      if (!lien) return
      el.classList.add('--nav-lock')
      if (globalStorage.menuOpen) this.close()
      this.navMobile?.reset?.()
    })
    window.addEventListener('mousemove', (e) => {
      if (!el.classList.contains('--nav-lock')) return
      if (!el.contains(e.target)) el.classList.remove('--nav-lock')
    }, { passive: true })

    this.lastPointer = { x: -1, y: -1 }
    window.addEventListener('scroll', () => {
      el.classList.add('--scrolling')
    }, { passive: true })
    window.addEventListener('mousemove', (e) => {
      const moved = Math.abs(e.clientX - this.lastPointer.x) > 2 || Math.abs(e.clientY - this.lastPointer.y) > 2
      this.lastPointer.x = e.clientX
      this.lastPointer.y = e.clientY
      if (moved && el.classList.contains('--scrolling')) el.classList.remove('--scrolling')
    }, { passive: true })

    // Accumulateur directionnel (remplace l'Observer onUp/onDown, trop nerveux même
    // avec tolérance : l'inertie Lenis oscille au changement de direction et le header
    // "pompait" en ressort — retours Sandro/Amélie). Il faut 90px de descente CONTINUE
    // pour masquer, 40px de remontée CONTINUE pour montrer ; toute inversion remet le
    // compteur à zéro : les micro-oscillations ne peuvent plus faire basculer.
    this.lastScrollY = window.scrollY
    this.scrollAcc = 0
    window.addEventListener('scroll', () => {
      const y = window.scrollY
      const d = y - this.lastScrollY
      this.lastScrollY = y
      if (d === 0) return
      if ((d > 0) !== (this.scrollAcc > 0)) this.scrollAcc = 0
      this.scrollAcc += d
      if (this.scrollAcc > 90 && y > 150 && el.classList.contains('--visible')) {
        el.classList.remove('--visible')
        this.scrollAcc = 0
      } else if (this.scrollAcc < -40 && !el.classList.contains('--visible')) {
        el.classList.add('--visible')
        this.scrollAcc = 0
      }
    }, { passive: true })

    this.scrolledObserver = Observer.create({
      target: window,
      type: 'scroll',
      onChange: (self) => {
        // Transition d'état du header (compact <-> complet) : la barre BOUGE sous une
        // souris immobile (la topbar rentre/sort, ~50px) -> hover involontaire -> le menu
        // s'ouvrait "tout seul, pas partout, pas tout le temps" (retour Sandro 9/07, 1h).
        // Pendant le mouvement (--settling ~550ms), le hover est desarme.
        const settle = () => {
          this.DOM.el.classList.add('--settling')
          clearTimeout(this.settleTimeout)
          this.settleTimeout = setTimeout(() => this.DOM.el.classList.remove('--settling'), 550)
        }
        if (window.scrollY > 20 && !body.classList.contains('--scrolled')) { body.classList.add('--scrolled'); settle() }
        if (window.scrollY <= 20 && body.classList.contains('--scrolled')) { body.classList.remove('--scrolled'); settle() }
      }
    })
  }

  onResize() {
    clearTimeout(this.resizeTimeout)

    this.resizeTimeout = setTimeout(() => {
      if (this.windowWidthResize !== window.innerWidth) {
        this.windowWidthResize = window.innerWidth
        if (globalStorage.menuOpen) this.close()
        this.navMobile?.reset()
        this.resetAllMegamenuPanels()
        this.positionAllMegamenus()
      }
    }, 250)
  }
}
